"use client";

import React, { Component, type ReactNode, type ErrorInfo } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const DefaultFallback = ({ error }: { error: Error | null }) => {
  const t = useTranslations("error");
  const locale = useLocale();
  return (
    <div
      className="p-4 bg-red-50 border border-red-200 rounded-md"
      data-testid="error-boundary"
    >
      <h2 className="text-lg font-semibold text-red-700 mb-2">{t("title")}</h2>
      <p className="text-red-600 mb-2">{t("description")}</p>
      <p className="text-red-600 mb-4">{error?.message}</p>
      <div className="flex space-x-2">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200"
        >
          {t("try_again")}
        </button>
        <Link
          href={`/${locale}`}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
        >
          {t("back_to_home")}
        </Link>
      </div>
    </div>
  );
};

export class ErrorBoundaryWrapper extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // 更新状态，下次渲染时显示备用 UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundaryWrapper 捕获到错误:", error, errorInfo);

    // 添加错误状态标记，帮助测试脚本识别
    document.body.setAttribute("data-error-boundary", "true");
    document.body.setAttribute("data-error-message", error.message);

    // 特别处理 "Unsupported Server Component type: Null" 错误
    if (error.message.includes("Unsupported Server Component type: Null")) {
      console.log("检测到 Next.js 服务器组件 Null 类型错误，尝试恢复...");

      // 强制刷新页面
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // 如果提供了自定义的 fallback，则使用它
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // 默认的错误 UI
      return <DefaultFallback error={this.state.error} />;
    }

    return this.props.children;
  }
}

// 创建一个高阶组件，用于包装页面组件
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundaryWrapper fallback={fallback}>
        <Component {...props} />
      </ErrorBoundaryWrapper>
    );
  };
}

export default ErrorBoundaryWrapper;
